/**
 * N1 · Details step 1 of 4 — NAME only.
 *
 * Validators and the printed-name derivation come from @/lib/details-steps, the
 * one place that decides whether any details step is complete.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readDetails, readSelectedVariant, saveDetails } from "@/lib/consumer-session";
import { Field, SectionTitle, inputClass } from "@/components/consumer/chrome";
import DetailsStepPage from "@/components/consumer/DetailsStepPage";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S6_SECTION, S6_DEFAULTS } from "@/content/screens/s6";
import { nameMissing, nextDetailsStep, prevDetailsStep, printedName } from "@/lib/details-steps";
import { useFieldCompletion } from "@/hooks/useFieldCompletion";
import { useKnownDetailsPrefill } from "@/lib/details-prefill";
import { useConsumerFlow } from "../../ConsumerFlow";

export default function NameStep() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const { t } = useSiteContent(S6_SECTION, S6_DEFAULTS);
  const existing = readDetails(program.slug);

  const [firstName, setFirstName] = useState(existing?.first_name ?? "");
  const [middleName, setMiddleName] = useState(existing?.middle_name ?? "");
  const [lastName, setLastName] = useState(existing?.last_name ?? "");

  useEffect(() => {
    if (!readSelectedVariant(program.slug)) navigate("../reveal", { replace: true });
  }, [program.slug, navigate]);

  /* PREFILL from what we already hold (profile first). Every field stays
     editable and an edit is saved to the NEW order only. */
  useKnownDetailsPrefill(program.slug, (known) => {
    setFirstName((v) => v || known.first_name || "");
    setLastName((v) => v || known.last_name || "");
    setMiddleName((v) => v || known.middle_name || "");
  });

  const missing = nameMissing(firstName, lastName);
  // The field IS the step here, but the completion row still separates "arrived
  // and typed a name" from "arrived and left".
  useFieldCompletion("field_name_complete", !missing);

  function onContinue() {
    saveDetails(program.slug, {
      ...(readDetails(program.slug) ?? {}),
      first_name: firstName.trim(),
      middle_name: middleName.trim() || null,
      last_name: lastName.trim(),
    } as any);
    navigate(nextDetailsStep("name"));
  }

  return (
    <DetailsStepPage
      testId="s6-name"
      backTo={prevDetailsStep("name")}
      backLabel={t("s6.header.backLabel")}
      step="1 of 4"
      title={t("s6.name.title")}
      missing={missing}
      continueLabel={t("s6.footer.continue")}
      onContinue={onContinue}
    >
      <SectionTitle className="sr-only">{t("s6.name.title")}</SectionTitle>
      <p className="mb-4 text-[14px] leading-[1.5] text-foreground/60">{t("s6.name.hint")}</p>

      <div className="flex flex-col gap-3.5">
        <Field label={t("s6.name.first")}>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            placeholder={t("s6.name.firstPlaceholder")}
            className={inputClass}
            data-testid="s6-first-name"
          />
        </Field>
        <Field
          label={
            (
              <span className="flex items-center gap-2">
                {t("s6.name.middle")}
                <span className="rounded-full bg-foreground/[0.07] px-2 py-[3px] text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-muted">
                  {t("s6.name.optionalBadge")}
                </span>
              </span>
            ) as unknown as string
          }
        >
          <input
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            autoComplete="additional-name"
            placeholder={t("s6.name.middlePlaceholder")}
            className={inputClass}
            data-testid="s6-middle-name"
          />
        </Field>
        <Field label={t("s6.name.last")}>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            placeholder={t("s6.name.lastPlaceholder")}
            className={inputClass}
            data-testid="s6-last-name"
          />
        </Field>
      </div>

      <div className="mt-5 flex items-center gap-3 rounded-[14px] bg-ink px-4 py-[13px]">
        <span className="flex-none text-[11px] font-semibold uppercase tracking-[0.1em] text-paper/50">
          {t("s6.name.printsAs")}
        </span>
        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[15px] font-medium tracking-[0.08em] text-white">
          {printedName(firstName, middleName, lastName)}
        </span>
      </div>

      {/*
        COUNSEL-OWNED, VERBATIM. The identity-data disclosure belongs at the point
        identity is entered, not only at payment. Its vocabulary matches
        `s7.legal.handoff`: "a participating bank", the CATEGORY, never a named
        bank — the recipient is resolved server-side after consent. Do not
        paraphrase, shorten or split.
      */}
      <p
        className="mt-5 text-[13px] leading-[1.55] text-foreground/65"
        data-testid="s6-legal-notice"
      >
        {t("s6.legal.notice")}
      </p>
    </DetailsStepPage>
  );
}
